<template>
	<div>
		<div class="row gutter-sm q-mb-md">
			<div class="col-xs-12 col-sm-12">
				<q-field :count="256">
					<q-input v-model="title" ref="title" :float-label="$t('title')" :error="$v.title.$error"
						@input="$v.title.$touch" :disable="!manageWebcasts" />
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-field :count="1024">
					<q-input type="textarea" v-model="description" ref="description" :float-label="$t('description')"
						:error="$v.description.$error" @input="$v.description.$touch" :disable="!manageWebcasts" />
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="isChevrolet">
				<q-input type="text" v-model="code" :float-label="$t('training_code')" :disable="!manageWebcasts" />
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select v-if="canSetProvider" v-model="webcast_type" :options="getWebcastTypes"
					:float-label="$t('stream_type')" chips autofocus-filter :disable="!manageWebcasts" />
				<q-select v-else disable readonly filled hint="Disable and readonly" v-model="webcast_type"
					:options="getWebcastTypes" :float-label="$t('stream_type')" chips autofocus-filter />
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-input v-if="webcast_type !== 2" v-model="embed" ref="embed" :float-label="$t('transmission_url')"
					:error="$v.embed.$error" @input="$v.embed.$touch" :disable="!manageWebcasts" />
			</div>

			<div :class="hasMinWatchTimePercentage ? 'col-xs-6 col-sm-6' : 'col-xs-12'"
				v-if="selected_client_params.webcast_use_score">
				<q-input v-model="score" :float-label="$t('score')" type="number"
					v-if="!selected_client_params.webcast_use_static_score && canSetScore" :disable="!manageWebcasts" />

				<q-select v-model="score" :options="scoresOptions" :float-label="$t('score')"
					v-if="selected_client_params.webcast_use_static_score && canSetScore" :disable="!manageWebcasts" />
			</div>

			<div class="col-xs-6 col-sm-6" v-if="hasMinWatchTimePercentage">
				<q-field :helper="`${$t('the_percentage_must_be_between_5_and_8')}`">
					<q-input v-model="min_watch_time_percentage" :float-label="$t('min_attendance_for_certification')"
						type="number" :disable="!manageWebcasts || disableMinWatchTimePercentage || has_results" :error="$v.min_watch_time_percentage.$error" />
				</q-field>
			</div>

			<div class="col-12">
				<q-select v-model="timezone" ref="timezone" :options="timezones" :float-label="$t('timezone')" filter
					:error="$v.timezone.$error" @input="$v.timezone.$touch" :disable="!manageWebcasts" />
			</div>

			<div class="col-xs-6 col-sm-6">
				<q-datetime v-model="transmission_date" ref="transmission_date" type="datetime"
					:float-label="$t('transmission_date')" format="DD/MM/YYYY HH:mm"
					:error="$v.transmission_date.$error" @input="$v.transmission_date.$touch"
					:disable="!manageWebcasts" />
			</div>

			<div class="col-xs-6 col-sm-6">
				<q-input type="number" v-model="workload" ref="workload" :float-label="$t('workload_in_hours')"
					:error="$v.workload.$error" @input="$v.workload.$touch" :disable="!manageWebcasts" />
			</div>

			<div class="col-xs-12 col-sm-6">
				<p class="caption">{{ $t('use_chat') }}</p>
				<q-btn-toggle v-model="use_chat" :toggle-color="getColorUseChat" :options="chat_options"
					:disable="!manageWebcasts" />
			</div>

			<div class="col-xs-12 col-sm-6">
				<p class="caption">{{ $t('chat_moderated') }}</p>
				<q-btn-toggle v-model="chat_moderated" :toggle-color="getColorChatModerated" :options="chat_options"
					:disable="disableChatModerated || !manageWebcasts" />
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select v-model="tag" :options="optionTags" :float-label="$t('tags')" multiple chips filter
					autofocus-filter :disable="!manageWebcasts" />
			</div>

			<div class="col-xs-12 col-sm-12">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('image') }}</p>
						<uploader content_type="images" @fileUploaded="imageUploaded" :multiple="false" :cropper="true"
							:aspect_ratio="52 / 29" :aspect_ratio_string="'52/29'" :disable="!manageWebcasts" />

						<p v-if="webcast_images" class="q-mt-md file-url">
							<q-icon name="fal fa-check" color="positive" />
							<a :href="webcast_images" target="_blank">
								{{ $truncateLink(webcast_images) }}</a>
						</p>
					</div>
					<div class="col-xs-12 col-sm-6">
						<p class="caption">
							{{ $t('alt_image') }}
							<q-icon name="fal fa-question-circle" size="1.5rem" color="grey" class="q-ml-sm q-pb-sm">
								<q-tooltip class="custom-tooltip" anchor="top left" self="bottom right">
									{{ $t('alt_image_information_helper') }}
								</q-tooltip>
							</q-icon>
						</p>
						<div class="flex justify-between">
							<template v-for="lang in languages" class="row flex">
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'pt-br'">
									<q-input v-model="language_br" :float-label="$t(`${lang.name}`)" ref="language_br"
										:error="$v.language_br.$error" @input="$v.language_br.$touch" />
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'en-us'">
									<q-input v-model="language_us" :float-label="$t(`${lang.name}`)" ref="language_us"
										:error="$v.language_us.$error" @input="$v.language_us.$touch" />
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'es-es'">
									<q-input v-model="language_es" :float-label="$t(`${lang.name}`)" ref="language_es"
										:error="$v.language_es.$error" @input="$v.language_es.$touch" />
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'fr-fr'">
									<q-input v-model="language_fr" :float-label="$t(`${lang.name}`)" ref="language_fr"
										:error="$v.language_fr.$error" @input="$v.language_fr.$touch" />
								</q-field>
							</template>
						</div>
					</div>
				</div>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="webcast_lounge_templates_value.length > 0">
				<q-select chips v-model="webcast_lounge_template" :options="webcast_lounge_templates_value.map((e) => ({ value: e.id, label: e.title }))
					" :float-label="$t('webcast_lounge_template')" clearable />
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-field :helper="`${$t('date_to_unpublish')}`">
					<q-datetime v-model="unpublish_date" ref="unpublish_date" type="date"
						:float-label="$t('unpublish_date')" format="DD/MM/YYYY" clearable :disable="!manageWebcasts" />
				</q-field>
			</div>
		</div>

		<q-tabs v-model="tab" color="secondary" class="shadow-1">
			<q-tab slot="title" name="permissions" icon="fal fa-clipboard-list-check" :label="$t('permissions')" />

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('areas') }}</p>
						<q-checkbox class="q-mb-sm" v-model="all_areas" :val="true" :label="$t('select_all_areas')"
							@input="checkAllAreas()" :disable="!manageWebcasts" />

						<select-multiple :filters="area" :options="areas" @on_value="area = $event"
							:error="$v.area.$error" :input="$v.area.$touch" :disable="!manageWebcasts" />
					</div>

					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox v-model="all_profiles" :val="true" :label="$t('all')" @input="checkAll()"
									:disable="!manageWebcasts" />
							</div>

							<div class="col-xs-12 col-sm-4" v-for="(item, index) in profiles" :key="index"
								:class="getErrorClass($v.profile.$error)">
								<q-checkbox keep-color v-model="profile" ref="profile" :val="item.value"
									:label="item.label" @input="$v.profile.$touch"
									:color="getErrorColor($v.profile.$error)" :disable="!manageWebcasts" />
							</div>

							<div class="col-xs-12 col-sm-12">
								<q-select v-model="user_group" ref="user_group" :options="user_groups"
									:float-label="$t('groups')" :error="$v.user_group.$error"
									@input="$v.user_group.$touch" multiple chips :disable="!manageWebcasts" />
							</div>
						</div>
						<div class="row gutter-sm q-my-sm" v-if="id_webcast != 0">
							<div class="col-xs-12 col-sm-11">
								<q-input v-model="url_webcast_portal" :float-label="$t('link')" :disable="true" />
							</div>

							<div class="col-xs-12 col-sm-1 q-mt-lg">
								<q-btn dense color="primary" icon="fal fa-copy" class="full-width" @click="copyLink()">
									<q-tooltip>
										{{ $t('copy_link') }}
									</q-tooltip>
								</q-btn>
							</div>
						</div>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<div class="row gutter-sm q-mt-sm">
			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('status') }}</p>
				<q-btn-toggle v-model="status" :toggle-color="getColorStatus" :options="statuses"
					:disable="!manageWebcasts" />
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn color="tertiary" class="full-width" :label="$t('back')" icon="far fa-chevron-left"
					@click="$router.push({ name: 'admin_webcasts' })" />
			</div>

			<div class="col-xs-12 col-sm-3" v-if="showDuplicateButton">
				<q-btn color="info" class="full-width" :label="$t('duplicate_webcast')" icon="far fa-copy"
					@click="show_confirm_duplicate = true" />
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn color="secondary" class="full-width" :label="$t('save')" icon="far fa-save" @click="saveData()"
					:disable="!manageWebcasts" />
			</div>
		</div>
		<q-dialog v-model="show_confirm_duplicate" @ok="duplicateWebcast">
			<span slot="title">{{ $t('duplicate_webcast') }}</span>

			<span slot="message">{{
					$t('confirm_duplicate_the_webcast_and_all_its_contents')
				}}</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="tertiary" :label="$t('cancel')" @click="show_confirm_duplicate = false" />
				<q-btn color="positive" :label="$t('confirm')" @click="duplicateWebcast()" />
			</template>
		</q-dialog>

		<q-dialog v-model="confirm_end" @ok="endWebcast()">
			<span slot="title">{{ $t('end_webcast') }}</span>

			<span slot="message">{{ $t('confirm_end_webcast') }}</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_end = false" />
				<q-btn color="negative" :label="$t('end')" @click="endWebcast()" />
			</template>
		</q-dialog>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, url, maxLength, minValue, maxValue } from 'vuelidate/lib/validators'
import Uploader from 'components/Uploader'
import { date } from 'quasar'
const { formatDate, addToDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import SelectMultiple from 'components/utils/SelectMultiple'
export default {
	props: ['manageWebcasts'],

	data() {
		return {
			language_br: '',
			language_us: '',
			language_es: '',
			language_fr: '',
			id_webcast: '',
			title: '',
			show_confirm_duplicate: false,
			description: '',
			webcast_type: 1,
			webcast_player_hls_playback_url: '',
			embed: '',
			code: null,
			canSetProvider: true,
			status: 1,
			statuses: [
				{ label: this.$t('published'), value: 1 },
				{ label: this.$t('unpublished'), value: 2 },
				{ label: this.$t('finished'), value: 4 },
			],
			area: [],
			profile: [],
			user_group: [],
			all_profiles: false,
			use_chat: true,
			chat_moderated: true,
			transmission_date: null,
			transmission_completed: 0,
			tab: 'permissions',
			chat_options: [
				{ value: true, label: this.$t('yes') },
				{ value: false, label: this.$t('no') },
			],
			selected_client_params: [],
			score: 0,
			tag: [],
			webcast_images: '',
			confirm_end: false,
			timezone: '',
			all_areas: false,
			workload: 0,
			unpublish_date: null,
			webcast_lounge_template: null,
			min_watch_time_percentage: 80,
			has_results: false,
		}
	},

	components: {
		uploader: Uploader,
		'select-multiple': SelectMultiple,
	},

	computed: {
		...mapGetters({
			languages: 'params/languages',
			access_token: 'common/access_token',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			faq_categories: 'registration_lists/faq_categories',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			scores: 'registration_lists/webcast_scores',
			tags: 'registration_lists/tags',
			login_oauth2: 'params/login_oauth2',
			user_module_items: 'common/user_module_items',
			webcast_types: 'registration_lists/webcast_types',
			timezones: 'registration_lists/timezones',
			webcast_lounge_templates_value: 'registration_lists/webcast_lounge_templates',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		optionTags() {
			if (this.tags.length > 0) {
				return this.tags.map((t) => {
					let areas = t.areas.map((a) => a.name)
					return {
						value: t.value,
						label: t.label,
						sublabel: areas.join(', '),
					}
				})
			} else return []
		},

		getWebcastTypes() {
			if (this.webcast_types) {
				return this.webcast_types.map((type) => {
					return {
						value: type.value,
						label: type.label,
					}
				})
			}
		},

		getColorStatus() {
			if (this.status == 1) return 'positive'
			else if (this.status == 2) return 'warning'
			else if (this.status == 4) return 'negative'
			else return 'negative'
		},

		getColorUseChat() {
			if (this.use_chat == true) return 'positive'
			else return 'negative'
		},

		getColorChatModerated() {
			if (this.chat_moderated == true) return 'positive'
			else return 'negative'
		},

		disableChatModerated() {
			if (!this.use_chat) return true
			else return false
		},

		disableBasicData() {
			return this.status === 4 ? true : false
		},

		disableMinWatchTimePercentage() {
			return moment().isAfter(this.transmission_date)
		},

		getCurrentTimezone() {
			return date.formatDate(new Date(), 'Z')
		},

		canSetScore() {
			let obj = this.user_module_items.find((obj) => obj.slug === 'webcast_set_score')
			return obj ? obj.permission : false
		},

		isChevrolet() {
			if (process.env.PROJECT_FOLDER === 'universidadechevrolet') return true
			return false
		},

		url_webcast_portal() {
			let url = process.env.SITE_URL ? process.env.SITE_URL : document.location.origin
			return `${url}/webinars/${this.id_webcast}`
		},

		showDuplicateButton() {
			if (this.$route.params.id != 0) return true
			else return false
		},

		scoresOptions() {
			let scoreOpts = this.scores.map((e) => {
				return {
					value: e.score,
					label: e.label,
				}
			})
			let score = scoreOpts.find((e) => e.value == this.score)
			if (!score) {
				scoreOpts.push({
					value: this.score,
					label: this.score,
				})
			}
			return scoreOpts
		},

		hasMinWatchTimePercentage() {
			let client = this.clients.find(c => c.id === this.id_user_client_selected)
			return client.webcast_use_watch_time
		}
	},

	methods: {
		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_webcasts/webcasts/${this.id_webcast}/`, this.getHeader)
				.then((response) => {
					this.title = response.data.title
					this.description = response.data.description
					this.code = response.data.code
					this.webcast_type = response.data.webcast_type
					this.embed = response.data.embed
						? response.data.embed.includes('http')
							? response.data.embed
							: `https://${response.data.embed}`
						: ''
					this.status = response.data.status
					this.profile = response.data.profiles
					this.area = response.data.areas
					this.user_group = response.data.user_groups
					this.use_chat = response.data.use_chat
					this.chat_moderated = response.data.chat_moderated
					this.transmission_date = moment(response.data.transmission_date)
						.tz(response.data.timezone)
						.format('YYYY-MM-DDTHH:mm:ss')
					this.transmission_completed = response.data.transmission_completed
					this.score = parseInt(response.data.score)
					this.webcast_player_hls_playback_url =
						response.data.webcast_player_hls_playback_url
					this.tag = response.data.tags
					this.timezone = response.data.timezone
					this.workload = response.data.workload
					this.webcast_lounge_template = response.data.webcast_lounge_template
					this.has_results = response.data.has_results

					if (response.data.webcast_image_alt_texts) {
						const altTextMap = {}
						response.data.webcast_image_alt_texts.forEach((altTextObj) => {
							altTextMap[altTextObj.language] = altTextObj.alt_text
						})
						this.language_br = altTextMap['pt-br'] || ''
						this.language_us = altTextMap['en-us'] || ''
						this.language_es = altTextMap['es-es'] || ''
						this.language_fr = altTextMap['fr-fr'] || ''
					}

					if (response.data.min_watch_time_percentage) {
						this.min_watch_time_percentage = response.data.min_watch_time_percentage
					}

					let obj_img = response.data.webcast_images.find((obj) => {
						return obj.original === true
					})
					if (obj_img) this.webcast_images = obj_img.image

					if (this.webcast_type) this.canSetProvider = false

					this.unpublish_date = response.data.unpublish_date
						? date.formatDate(response.data.unpublish_date, 'YYYY-MM-DDT00:00:00.SSSZ')
						: null
					if (this.unpublish_date)
						this.unpublish_date = addToDate(this.unpublish_date, { days: 1 })

					this.$emit('loadContent', response.data)

					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				const data = {
					title: this.title,
					description: this.description,
					webcast_type: this.webcast_type,
					use_chat: this.use_chat,
					chat_moderated: this.chat_moderated,
					transmission_date: moment(
						date.formatDate(this.transmission_date, 'YYYY-MM-DDTHH:mm')
					)
						.tz(this.timezone, true)
						.format('YYYY-MM-DDTHH:mm:ssZ'),
					transmission_completed: this.transmission_completed,
					status: this.status,
					score: this.score
						? !isNaN(this.score)
							? this.score
							: parseInt(this.score)
						: 0,
					webcast_images: this.webcast_images,
					timezone: this.timezone,
					workload: this.workload,
					webcast_lounge_template: this.webcast_lounge_template
						? this.webcast_lounge_template
						: '',
					embed:
						this.webcast_type !== 2
							? this.embed.includes('http')
								? this.embed
								: `https://${this.embed}`
							: 'none',
					tags: this.tag,
					areas: this.area.length > 0 ? this.area : [],
					profiles: this.profile.length > 0 ? this.profile : [],
					user_groups: this.user_group.length > 0 ? this.user_group : [],
					code: this.code,
					webcast_image_alt_texts: {
						'pt-br': this.language_br ? this.language_br : '',
						'en-us': this.language_us ? this.language_us : '',
						'es-es': this.language_es ? this.language_es : '',
						'fr-fr': this.language_fr ? this.language_fr : '',
					},
				}

				if (this.unpublish_date) {
					data.unpublish_date = date.formatDate(this.unpublish_date, 'YYYY-MM-DD')
				}

				if (this.hasMinWatchTimePercentage) {
					data.min_watch_time_percentage = this.min_watch_time_percentage
				}

				const apiUrl =
					this.id_webcast != 0
						? `/admin_webcasts/webcasts/${this.id_webcast}/`
						: `/admin_webcasts/webcasts/`

				const method = this.id_webcast != 0 ? 'put' : 'post'

				this.$axios[method](apiUrl, data, this.getHeader)
					.then((response) => {
						if (this.id_webcast == 0) {
							this.id_webcast = response.data.id
							this.$router.push({
								name: 'admin_webcast',
								params: { id: response.data.id },
							})
							this.$emit('loadContent', response.data)
							this.$q.notify({
								message: this.$t('webcast_add_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})
						} else {
							this.$q.notify({
								message: this.$t('webcast_edit_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})
						}
					})
					.catch((e) => {
						console.log(e)
					})
					.finally(() => {
						this.$q.loading.hide()
					})
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fal fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		getErrorColor(state) {
			return state ? 'negative' : ''
		},

		getErrorClass(state) {
			return state ? 'checkbox-error' : ''
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles.forEach((element) => {
					this.profile.push(element.value)
				})
			} else {
				this.profile = []
			}
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.area = this.areas.map((e) => e.value)
			} else {
				this.area = []
			}
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		imageUploaded(url) {
			this.webcast_images = url
		},

		confirmEndWebcast() {
			this.confirm_end = true
		},

		endWebcast() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				let fd = new FormData()
				fd.append('title', this.title)
				fd.append('description', this.description)
				fd.append('webcast_type', this.webcast_type)
				fd.append('embed', this.embed)
				fd.append('use_chat', this.use_chat)
				fd.append('chat_moderated', this.chat_moderated)
				fd.append(
					'transmission_date',
					date.formatDate(this.transmission_date, 'YYYY-MM-DD HH:mm:ss')
				)
				fd.append('transmission_completed', this.transmission_completed)
				fd.append('status', 4)
				fd.append('score', this.score)
				fd.append('webcast_images', this.webcast_images)
				fd.append('workload', this.workload)
				if (this.unpublish_date)
					fd.append('unpublish_date', date.formatDate(this.unpublish_date, 'YYYY-MM-DD'))

				this.tag.forEach((tag) => {
					fd.append('tags', tag)
				})

				if (this.tab === 'permissions') {
					this.area.forEach((area) => {
						fd.append('areas', area)
					})

					this.profile.forEach((profile) => {
						fd.append('profiles', profile)
					})
				} else if (this.tab === 'groups') {
					this.user_group.forEach((user_group) => {
						fd.append('user_groups', user_group)
					})
				}

				if (this.id_webcast != 0) {
					this.$axios
						.put(`/admin_webcasts/webcasts/${this.id_webcast}/`, fd, this.getHeader)
						.then(() => {
							this.$q.loading.hide()

							this.$q.notify({
								message: this.$t('webcast_end_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})

							this.$router.push({ name: 'admin_webcasts' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				} else {
					this.$q.loading.hide()
				}
			}
		},

		fetchTimezones() {
			let values = []

			moment.tz
				.names()
				.reduce((memo, tz) => {
					memo.push({
						name: tz,
						offset: moment.tz(tz).utcOffset(),
					})

					return memo
				}, [])
				.sort((a, b) => {
					return a.offset - b.offset
				})
				.reduce((memo, tz) => {
					const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
					values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
				}, '')

			this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
		},

		duplicateWebcast() {
			this.show_confirm_duplicate = false
			this.$q.loading.show()

			this.$axios
				.get(`/admin_webcasts/webcasts/${this.id_webcast}/duplicate/`, this.getHeader)
				.then((response) => {
					this.$router.push({
						name: 'admin_webcast',
						params: { id: response.data.webcast_duplicated_id },
					})

					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		copyLink() {
			this.$copyText(this.url_webcast_portal).then(
				() => {
					this.$q.notify({
						message: this.$t('link_copied_to_clipboard'),
						type: 'positive',
						icon: 'fal fa-copy',
					})
				},
				(e) => {
					console.log(e)
				}
			)
		},
	},

	created() {
		this.fetchTimezones()
		this.selected_client_params = this.clients.find(
			(obj) => obj.id === this.id_user_client_selected
		)
		this.id_webcast = this.$route.params.id

		if (this.id_webcast != 0) {
			this.fetchData()
		} else {
			this.timezone = moment.tz.guess()
		}
	},

	watch: {
		$route: function (refreshPage) {
			this.selected_client_params = this.clients.find(
				(obj) => obj.id === this.id_user_client_selected
			)
			this.id_webcast = this.$route.params.id
			if (this.id_webcast != 0) this.fetchData()
		},
	},

	validations() {
		return {
			title: { required, maxLength: maxLength(256) },
			language_br: { maxLength: maxLength(170) },
			language_us: { maxLength: maxLength(170) },
			language_es: { maxLength: maxLength(170) },
			language_fr: { maxLength: maxLength(170) },
			description: { maxLength: maxLength(1024) },
			webcast_type: { required },
			min_watch_time_percentage: { required, minValue: minValue(5), maxValue: maxValue(80) },
			embed: {
				requiredIf: requiredIf(() => {
					if (
						this.webcast_type === 1 ||
						this.webcast_type === 3 ||
						this.webcast_type === 5
					)
						return true
					else return false
				}),
			},
			transmission_date: { required },
			workload: { required },
			// unpublish_date: { minValue: (value) => value > new Date().toISOString() },
			area: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			profile: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			user_group: {
				requiredIf: requiredIf(() => {
					if (
						this.profile.length == 0 &&
						this.area.length == 0 &&
						this.user_group.length == 0
					) {
						return true
					} else {
						return false
					}
				}),
			},
			timezone: { required },
		}
	},
}
</script>

<style lang="stylus" scoped>
@import '~variables'

.file-url
    a
        text-decoration none
        color black
</style>
